import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plus, CheckCircle, Clock, XCircle, GraduationCap, Star, Calendar, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NIVEAUX_COMPETENCES = [
  { value: 'non_acquis', label: 'Non acquis', color: 'bg-red-100 text-red-700', stars: 1 },
  { value: 'en_cours', label: "En cours", color: 'bg-amber-100 text-amber-700', stars: 2 },
  { value: 'acquis', label: 'Acquis', color: 'bg-emerald-100 text-emerald-700', stars: 3 },
  { value: 'maitrise', label: 'Maîtrisé', color: 'bg-blue-100 text-blue-700', stars: 4 }
];

const FreinsDetaillesModule = ({ bénéficiaireId }) => {
  const [freins, setFreins] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  const [ateliers, setAteliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [selectedFrein, setSelectedFrein] = useState(null);
  const [newFrein, setNewFrein] = useState({ categorie: 'Mobilité', commentaire: '' });
  const [updateData, setUpdateData] = useState({ statut: '', commentaire: '' });

  // Quand on sélectionne un frein, pré-remplir updateData avec ses valeurs actuelles
  useEffect(() => {
    if (selectedFrein) {
      setUpdateData({
        statut: selectedFrein.statut || 'identifie',
        commentaire: ''
      });
    }
  }, [selectedFrein]);

  useEffect(() => {
    loadFreins();
    loadDiagnostic();
    loadAteliers();
  }, [bénéficiaireId]);

  const loadFreins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/freins-detailles/${bénéficiaireId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreins(response.data);
    } catch (error) {
      console.error('Erreur chargement freins', error);
    }
  };

  const loadDiagnostic = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnostic-competences/apprenant/${bénéficiaireId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostic(response.data);
    } catch (error) {
      console.error('Erreur chargement diagnostic', error);
    }
  };

  const loadAteliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ateliers/apprenant/${bénéficiaireId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAteliers(response.data || []);
    } catch (error) {
      console.error('Erreur chargement ateliers', error);
    }
  };

  const handleAddFrein = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/freins-detailles`, {
        apprenant_id: bénéficiaireId,
        ...newFrein
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Frein ajouté');
      setOpenAdd(false);
      setNewFrein({ categorie: 'Mobilité', commentaire: '' });
      loadFreins();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFrein = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/freins-detailles/${selectedFrein.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Frein mis à jour');
      setOpenUpdate(false);
      setUpdateData({ statut: '', commentaire: '' });
      loadFreins();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'actif': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'en_cours': return <Clock className="w-5 h-5 text-amber-500" />;
      case 'leve': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      default: return null;
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'actif': return 'bg-red-100 text-red-700';
      case 'en_cours': return 'bg-amber-100 text-amber-700';
      case 'leve': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getNiveauInfo = (value) => NIVEAUX_COMPETENCES.find(n => n.value === value) || NIVEAUX_COMPETENCES[0];

  // Calculer les statistiques du diagnostic
  const getDiagnosticStats = () => {
    if (!diagnostic?.evaluations) return null;
    const evals = diagnostic.evaluations;
    const total = evals.length;
    const acquis = evals.filter(e => e.niveau_auto === 'acquis' || e.niveau_auto === 'maitrise').length;
    const enCours = evals.filter(e => e.niveau_auto === 'en_cours').length;
    const nonAcquis = evals.filter(e => e.niveau_auto === 'non_acquis').length;
    return { total, acquis, enCours, nonAcquis };
  };

  // Filtrer les ateliers à venir
  const getAteliersAVenir = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return ateliers.filter(atelier => {
      const atelierDate = new Date(atelier.date);
      return atelierDate >= today;
    }).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);
  };

  const diagnosticStats = getDiagnosticStats();
  const ateliersAVenir = getAteliersAVenir();

  return (
    <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Diagnostics des Freins
        </CardTitle>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un frein
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>Nouveau Frein</DialogTitle>
              <DialogDescription>Identifier un nouveau frein périphérique</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={newFrein.categorie} onValueChange={(val) => setNewFrein({ ...newFrein, categorie: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mobilité">Mobilité</SelectItem>
                    <SelectItem value="Logement">Logement</SelectItem>
                    <SelectItem value="Financier">Financier</SelectItem>
                    <SelectItem value="Santé">Santé</SelectItem>
                    <SelectItem value="Juridique">Juridique</SelectItem>
                    <SelectItem value="Addiction">Addiction</SelectItem>
                    <SelectItem value="Administratif">Administratif</SelectItem>
                    <SelectItem value="Autres">Autres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Commentaire initial</Label>
                <Textarea
                  value={newFrein.commentaire}
                  onChange={(e) => setNewFrein({ ...newFrein, commentaire: e.target.value })}
                  className="rounded-xl"
                  placeholder="Décrivez le frein..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleAddFrein} disabled={loading} className="rounded-xl">
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {freins.map((frein) => (
            <div key={frein.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getStatusIcon(frein.statut)}
                  <div>
                    <div className="font-bold text-slate-900">{frein.categorie}</div>
                    <Badge className={`${getStatusColor(frein.statut)} mt-1`}>
                      {frein.statut === 'actif' ? 'Actif' : frein.statut === 'en_cours' ? 'En cours' : 'Levé'}
                    </Badge>
                  </div>
                </div>
                {frein.statut !== 'leve' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedFrein(frein);
                      setUpdateData({ statut: frein.statut, commentaire: '' });
                      setOpenUpdate(true);
                    }}
                    className="rounded-xl"
                  >
                    Mettre à jour
                  </Button>
                )}
              </div>
              
              {frein.historique && frein.historique.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="text-sm font-bold text-slate-500">Timeline</div>
                  {frein.historique.map((entry, idx) => (
                    <div key={idx} className="pl-4 border-l-2 border-blue-200 py-2">
                      <div className="text-xs text-slate-500">{new Date(entry.date).toLocaleDateString('fr-FR')} - {entry.auteur}</div>
                      <div className="text-sm text-slate-700">{entry.commentaire}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <Dialog open={openUpdate} onOpenChange={setOpenUpdate}>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>Mettre à jour le frein</DialogTitle>
              <DialogDescription>{selectedFrein?.categorie}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nouveau statut</Label>
                <Select value={updateData.statut} onValueChange={(val) => setUpdateData({ ...updateData, statut: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="leve">Levé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Commentaire</Label>
                <Textarea
                  value={updateData.commentaire}
                  onChange={(e) => setUpdateData({ ...updateData, commentaire: e.target.value })}
                  className="rounded-xl"
                  placeholder="Décrivez l'évolution..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleUpdateFrein} disabled={loading} className="rounded-xl">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>

      {/* Section Résumé Diagnostic de Compétences */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <GraduationCap className="w-6 h-6 text-blue-600" />
            Résumé du Diagnostic de Compétences
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!diagnostic ? (
            <div className="text-center py-6 text-slate-500">
              <GraduationCap className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>Aucun diagnostic de compétences réalisé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Statut du diagnostic */}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Statut</span>
                <Badge className={
                  diagnostic.statut === 'valide' 
                    ? 'bg-emerald-100 text-emerald-700'
                    : diagnostic.statut === 'soumis'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600'
                }>
                  {diagnostic.statut === 'valide' ? 'Validé' : diagnostic.statut === 'soumis' ? 'En attente de validation' : 'En cours'}
                </Badge>
              </div>

              {/* Statistiques globales */}
              {diagnosticStats && (
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{diagnosticStats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{diagnosticStats.acquis}</p>
                    <p className="text-xs text-emerald-600">Acquis/Maîtrisé</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{diagnosticStats.enCours}</p>
                    <p className="text-xs text-amber-600">En cours</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{diagnosticStats.nonAcquis}</p>
                    <p className="text-xs text-red-600">Non acquis</p>
                  </div>
                </div>
              )}

              {/* Top compétences (maîtrisées et à améliorer) */}
              {diagnostic.evaluations && diagnostic.evaluations.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {/* Compétences maîtrisées */}
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" />
                      Points forts
                    </h4>
                    <div className="space-y-1">
                      {diagnostic.evaluations
                        .filter(e => e.niveau_auto === 'maitrise' || e.niveau_auto === 'acquis')
                        .slice(0, 3)
                        .map((e, idx) => (
                          <p key={idx} className="text-sm text-emerald-700 truncate">
                            • {e.competence}
                          </p>
                        ))
                      }
                      {diagnostic.evaluations.filter(e => e.niveau_auto === 'maitrise' || e.niveau_auto === 'acquis').length === 0 && (
                        <p className="text-sm text-emerald-600 italic">Aucune compétence acquise</p>
                      )}
                    </div>
                  </div>

                  {/* Compétences à améliorer */}
                  <div className="bg-amber-50 rounded-xl p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      À développer
                    </h4>
                    <div className="space-y-1">
                      {diagnostic.evaluations
                        .filter(e => e.niveau_auto === 'non_acquis' || e.niveau_auto === 'en_cours')
                        .slice(0, 3)
                        .map((e, idx) => (
                          <p key={idx} className="text-sm text-amber-700 truncate">
                            • {e.competence}
                          </p>
                        ))
                      }
                      {diagnostic.evaluations.filter(e => e.niveau_auto === 'non_acquis' || e.niveau_auto === 'en_cours').length === 0 && (
                        <p className="text-sm text-amber-600 italic">Toutes les compétences acquises</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Date de validation si validé */}
              {diagnostic.statut === 'valide' && diagnostic.valide_par_nom && (
                <p className="text-xs text-slate-500 text-right mt-2">
                  Validé par {diagnostic.valide_par_nom} le {new Date(diagnostic.date_validation).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

export default FreinsDetaillesModule;